// src/pages/PrivacyPolicyPage.tsx - Valuate Privacy Policy
import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const sectionStyle = {
    mb: 4,
  };

  const headingStyle = {
    fontWeight: 600,
    mb: 2,
    color: theme.palette.text.primary,
  };

  const textStyle = {
    color: theme.palette.text.secondary,
    lineHeight: 1.8,
    mb: 2,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
          : 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 4 }}
        >
          Back to Home
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 4 }}>
            Privacy Policy
          </Typography>

          <Typography sx={{ ...textStyle, mb: 4 }}>
            This Privacy Policy describes how Valuate ("we," "us," or "our") collects,
            uses, and protects information when you use our platform.
          </Typography>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              1. Information We Collect
            </Typography>
            <Typography sx={textStyle}>
              <strong>Account Information:</strong> When you create an account, we collect
              your email address, name, password (stored as a secure hash), phone number,
              job title, company, years of experience, certifications, areas of expertise,
              LinkedIn profile, and communication preferences.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Hotel and Property Data:</strong> We collect and process information
              about hotels you add to the platform, including property details, financial
              documents (P&L statements, tax documents, STR reports), and property images.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Uploaded Documents:</strong> We process financial documents in various
              formats (PDF, Excel, CSV, images) that you upload for valuation analysis.
              These documents are stored securely and processed by our AI systems.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Usage Data:</strong> We collect information about how you interact
              with our platform, including features used, analysis requests, valuations
              generated, and conversation history with our AI assistant.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              2. How We Use Your Information
            </Typography>
            <Typography sx={textStyle}>
              We use collected information to: provide and improve our valuation services;
              process and analyze uploaded documents using AI; generate property valuations
              and market analyses; maintain and secure your account; communicate with you
              about our services; and comply with legal obligations.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              3. Data Storage and Security
            </Typography>
            <Typography sx={textStyle}>
              <strong>Database:</strong> Your account and property data is stored in a
              PostgreSQL database hosted on Railway (US-based infrastructure).
            </Typography>
            <Typography sx={textStyle}>
              <strong>Document Storage:</strong> Uploaded documents are stored in Amazon
              Web Services (AWS) S3 in the us-east-2 region.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Security Measures:</strong> We implement industry-standard security
              including password hashing (bcrypt), encrypted connections (HTTPS), and
              secure authentication tokens. Data is transmitted over encrypted connections.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              4. Third-Party Services
            </Typography>
            <Typography sx={textStyle}>
              <strong>Google Gemini AI:</strong> We use Google's Gemini AI for document
              analysis, text extraction, and generating valuations. Document content is
              sent to Google's AI services for processing.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Amazon Web Services (AWS):</strong> We use AWS S3 for secure document
              storage, subject to AWS's data processing terms.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Google Drive Integration:</strong> If you connect Google Drive,
              we access files you explicitly select for import. This integration is
              governed by Google's privacy policy.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              5. Your Rights
            </Typography>
            <Typography sx={textStyle}>
              <strong>Right to Access (Data Portability):</strong> You can download a
              complete copy of all your personal data at any time through Settings &gt;
              Privacy & Data. Your export includes your profile, all hotels, financial
              data, documents, and analysis history in JSON format.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Right to Deletion (Right to be Forgotten):</strong> You can delete
              your account through Settings &gt; Privacy & Data. After requesting deletion,
              you have a 7-day grace period to cancel. After 7 days, your account and all
              associated data will be permanently deleted, including: your profile, all
              hotels, documents stored in S3, analyses, valuations, and conversation history.
              This action cannot be undone.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Right to Rectification:</strong> You can update your personal
              information at any time through your Profile page.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              6. Data Retention
            </Typography>
            <Typography sx={textStyle}>
              <strong>Active Accounts:</strong> We retain your data for as long as your
              account is active.
            </Typography>
            <Typography sx={textStyle}>
              <strong>Deleted Accounts:</strong> When you request account deletion, there
              is a 7-day grace period during which you can cancel. After 7 days, all data
              is permanently deleted from our systems.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              7. Cookies
            </Typography>
            <Typography sx={textStyle}>
              We use essential cookies and local storage to maintain your authentication
              session and remember your preferences (such as theme settings). We do not
              use third-party advertising cookies.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              8. Changes to This Policy
            </Typography>
            <Typography sx={textStyle}>
              We may update this Privacy Policy from time to time. We will notify you of
              significant changes by posting a notice on our platform or sending you an email.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              9. Contact Us
            </Typography>
            <Typography sx={textStyle}>
              If you have questions about this Privacy Policy or our data practices,
              please contact us through the contact form on our website.
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.disabled, mt: 4 }}
          >
            Last updated: January 2026
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
